import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError, timer } from 'rxjs';
import { retry, retryWhen, mergeMap, finalize } from 'rxjs/operators';
import { SubscriptionService } from './subscription.service';

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  
  private countrySubject = new BehaviorSubject<string>(null);
  public country$ = this.countrySubject.asObservable();

  private currentFilialeSubject = new BehaviorSubject<any>(null);
  public currentFiliale$ = this.currentFilialeSubject.asObservable();

  private filialesSubject = new BehaviorSubject<any[]>([]);
  public filiales$ = this.filialesSubject.asObservable();

  private isLoadedSubject = new BehaviorSubject<boolean>(false);
  public isLoaded$ = this.isLoadedSubject.asObservable();

  constructor(private subscriptionService: SubscriptionService) {}

  /**
   * Initialise la localisation et les filiales au démarrage de l'app
   */
  initializeLocation(): void {
    // Vérifier d'abord si on a déjà les données dans localStorage
    const cachedFiliale = localStorage.getItem('currentFiliale');
    if (cachedFiliale) {
      try {
        const filiale = JSON.parse(cachedFiliale);
        this.currentFilialeSubject.next(filiale);
      } catch (e) {
       
      }
    }

    // Charger la localisation avec retry
    this.subscriptionService.getLocation()
      .pipe(
        retryWhen(errors =>
          errors.pipe(
            mergeMap((error, index) => {
              // Réessayer jusqu'à 3 fois
              if (index < 3) {
                const delai = (index + 1) * 1000; // 1s, 2s, 3s
                return timer(delai);
              }
              // Après 3 tentatives, propager l'erreur
              return throwError(error);
            })
          )
        ),
        finalize(() => '')
      )
      .subscribe({
        next: (data) => {
          if (data.success) {
            const countryCode = data.country_code;
            this.countrySubject.next(countryCode);
            // Charger les filiales après avoir obtenu le pays
            this.loadFiliales(countryCode);
          } else {
            console.error('⚠️ Impossible de déterminer la localisation.');
            this.loadFiliales(null);
          }
        },
        error: (err) => {
          console.error('❌ Erreur définitive lors de la récupération de la localisation:', err);
          // Même en cas d'erreur, charger les filiales
          this.loadFiliales(null);
        }
      });
  }

  /**
   * Charge les filiales et détermine la filiale courante
   */
  private loadFiliales(countryCode: string): void { 
    this.subscriptionService.getFiliales()
      .pipe(
        retryWhen(errors =>
          errors.pipe(
            mergeMap((error, index) => {
              // Réessayer jusqu'à 3 fois
              if (index < 3) {
                const delai = (index + 1) * 1000; // 1s, 2s, 3s
                return timer(delai);
              }
              // Après 3 tentatives, propager l'erreur
              console.error('❌ Échec définitif de getFiliales après 3 tentatives');
              return throwError(error);
            })
          )
        ),
        finalize(() => '')
      )
      .subscribe({
        next: (resp) => {
          if (resp['responseCode'] == 200) {
            const filiales = resp['data']; 
            this.filialesSubject.next(filiales);
            let currentFiliale = null;
            if (countryCode) {
              // Chercher la filiale correspondant au pays
              currentFiliale = filiales.find(f => {
                return f.code === countryCode;
              });
              
              if (!currentFiliale) {
                // Si pas trouvé, prendre la holding
                currentFiliale = filiales.find(f => {
                  return f.code === 'holding' || f.code === 'HOLDING';
                });
              }
            } else {
              // Pas de pays, prendre la holding par défaut
              currentFiliale = filiales.find(f => f.code === 'holding' || f.code === 'HOLDING');
            }

            if (currentFiliale) {
              this.currentFilialeSubject.next(currentFiliale);
              localStorage.setItem('currentFiliale', JSON.stringify(currentFiliale));
            } else {
              // En dernier recours, prendre la première filiale disponible
              if (filiales && filiales.length > 0) {
                this.currentFilialeSubject.next(filiales[0]);
                localStorage.setItem('currentFiliale', JSON.stringify(filiales[0]));
              }
            }

            // Marquer comme chargé
            this.isLoadedSubject.next(true);
          }
        },
        error: (err) => {
          this.isLoadedSubject.next(true);
        }
      });
  }

  /**
   * Permet de changer manuellement la filiale
   */
  setCurrentFiliale(filiale: any): void {
    this.currentFilialeSubject.next(filiale);
    localStorage.setItem('currentFiliale', JSON.stringify(filiale));
  }

  /**
   * Récupère la filiale courante (valeur synchrone)
   */
  getCurrentFiliale(): any {
    return this.currentFilialeSubject.value;
  }

  /**
   * Récupère le code pays (valeur synchrone)
   */
  getCountry(): string {
    return this.countrySubject.value;
  }
}